import { QualificationIds } from "constants/QualificationIds";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  DuplicateHandlingMode,
  ISkuQualificationRequest,
  ResponseOfSkuQualificationPreviewResponse,
  ResponseOfValidateCourseNumberResponse,
  SkuQualificationPreviewResponse,
  SkuQualificationRequest,
  ValidateCourseNumberRequest,
  ValidateCourseNumberResponse,
} from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";
import {
  distinctByQualificationAndProfession,
  mergeCourseNumberValidations,
  mergePreviews,
} from "./wizardSave";

type SubmitResult = { [FORM_ERROR]?: string } | undefined;

// The wizard fans one form out into N requests, so every gate the single-record form ran
// once now runs per request and its responses are folded back into one dialog payload.
// Order matches the form this replaces: course number, then bulk preview, then ACPE.
export const useQualificationSave = (
  sku: SkuFormProps["sku"],
  onSaved: () => void,
) => {
  const { skuService, qualificationService } = useGlobalContext();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();

  const [pendingRequests, setPendingRequests] = useState<
    ISkuQualificationRequest[]
  >([]);
  const [pendingDuplicateHandling, setPendingDuplicateHandling] = useState<
    DuplicateHandlingMode | undefined
  >();

  const [internalOpen, internalOpenActions] = useBoolean(false);
  const [internalLoading, internalLoadingActions] = useBoolean(false);
  const [
    internalData,
    setInternalData,
  ] = useState<ValidateCourseNumberResponse | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const [confirmOpen, confirmOpenActions] = useBoolean(false);
  const [confirmLoading, confirmLoadingActions] = useBoolean(false);
  const [
    preview,
    setPreview,
  ] = useState<SkuQualificationPreviewResponse | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [acpeOpen, acpeOpenActions] = useBoolean(false);

  const reset = () => {
    setPendingRequests([]);
    setPendingDuplicateHandling(undefined);
    setInternalData(null);
    setInternalError(null);
    setPreview(null);
    setConfirmError(null);
  };

  // Saved one at a time so a mid-way failure can say how many rows already landed instead
  // of leaving the count unknown.
  const save = async (
    requests: ISkuQualificationRequest[],
    duplicateHandling?: DuplicateHandlingMode,
  ): Promise<SubmitResult> => {
    let saved = 0;

    try {
      for (const request of requests) {
        await registerPromise(
          skuService.saveSkuQualification(
            sku.id!,
            SkuQualificationRequest.fromJS({ ...request, duplicateHandling }),
          ),
        );
        saved += 1;
      }
    } catch (err: unknown) {
      queryClient.invalidateQueries(["skuQualifications", sku.id!]);
      queryClient.invalidateQueries("skuService.getSkuQualificationSubjectAreas");

      return {
        [FORM_ERROR]:
          saved === 0
            ? translateError(err, t)
            : `${t({ id: "skuQualificationPartialSaveError" }, { saved, total: requests.length })} ${translateError(err, t)}`,
      };
    }

    queryClient.invalidateQueries(["skuQualifications", sku.id!]);
    // Invalidated by prefix, not by id: the splits ride inside the qualification request and
    // one save can touch every member of a group.
    queryClient.invalidateQueries("skuService.getSkuQualificationSubjectAreas");
    reset();
    onSaved();

    return undefined;
  };

  const gateAcpe = async (
    requests: ISkuQualificationRequest[],
    duplicateHandling?: DuplicateHandlingMode,
  ): Promise<SubmitResult> => {
    const needsAcpe = requests.some(
      (request) =>
        request.qualificationId === QualificationIds.Acpe && request.courseNumber,
    );

    if (needsAcpe) {
      setPendingRequests(requests);
      setPendingDuplicateHandling(duplicateHandling);
      acpeOpenActions.setTrue();
      return undefined;
    }

    return save(requests, duplicateHandling);
  };

  const gateBulkPreview = async (
    requests: ISkuQualificationRequest[],
  ): Promise<SubmitResult> => {
    const responses: ResponseOfSkuQualificationPreviewResponse[] = await registerPromise(
      Promise.all(
        requests.map((request) =>
          skuService.previewBulkSaveSkuQualifications(
            sku.id!,
            SkuQualificationRequest.fromJS(request),
          ),
        ),
      ),
    );

    const merged = mergePreviews(
      responses.map((response) => response.data ?? {}),
    );
    const usesApprovals = requests.some(
      (request) => request.useQualificationApprovals,
    );

    if (usesApprovals || (merged.existingQualifications?.length ?? 0) > 0) {
      setPreview(SkuQualificationPreviewResponse.fromJS(merged));
      setPendingRequests(requests);
      confirmOpenActions.setTrue();
      return undefined;
    }

    return gateAcpe(requests);
  };

  const gateCourseNumber = async (
    requests: ISkuQualificationRequest[],
  ): Promise<SubmitResult> => {
    const courseNumber = requests[0].courseNumber;

    if (courseNumber && courseNumber.trim() !== "") {
      const responses: ResponseOfValidateCourseNumberResponse[] = await registerPromise(
        Promise.all(
          distinctByQualificationAndProfession(requests).map((request) =>
            qualificationService.validateCourseNumber(
              new ValidateCourseNumberRequest({
                productId: sku.productId!,
                qualificationId: request.qualificationId,
                professionId: request.professionId,
                skuAvailabilityId: sku.skuAvailabilityId!,
                courseNumber,
                skuId: sku.id,
              }),
            ),
          ),
        ),
      );

      const merged = mergeCourseNumberValidations(
        responses.map((response) => response.data ?? {}),
      );

      if (merged.hasConflict) {
        setInternalData(ValidateCourseNumberResponse.fromJS(merged));
        setPendingRequests(requests);
        internalOpenActions.setTrue();
        return undefined;
      }
    }

    return gateBulkPreview(requests);
  };

  const submit = async (
    requests: ISkuQualificationRequest[],
  ): Promise<SubmitResult> => {
    if (requests.length === 0) {
      return undefined;
    }

    try {
      return await gateCourseNumber(requests);
    } catch (err: unknown) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const internalValidation = {
    open: internalOpen,
    loading: internalLoading,
    data: internalData,
    error: internalError,
    courseNumber: pendingRequests[0]?.courseNumber ?? "",
    onProceed: async () => {
      internalLoadingActions.setTrue();

      try {
        const result = await gateBulkPreview(pendingRequests);

        if (result?.[FORM_ERROR]) {
          setInternalError(result[FORM_ERROR]);
          return;
        }

        internalOpenActions.setFalse();
        setInternalData(null);
        setInternalError(null);
      } catch (err: unknown) {
        setInternalError(translateError(err, t));
      } finally {
        internalLoadingActions.setFalse();
      }
    },
    onCancel: () => {
      internalOpenActions.setFalse();
      reset();
    },
  };

  const bulkConfirm = {
    open: confirmOpen,
    loading: confirmLoading,
    preview,
    error: confirmError,
    // The dialog only reads this to switch on ACPE wording, so it reports whether any
    // record in the batch is ACPE.
    qualificationId: pendingRequests.some(
      (request) => request.qualificationId === QualificationIds.Acpe,
    )
      ? QualificationIds.Acpe
      : pendingRequests[0]?.qualificationId,
    usesApprovals: pendingRequests.some(
      (request) => request.useQualificationApprovals,
    ),
    onConfirm: async (duplicateHandling?: DuplicateHandlingMode) => {
      confirmLoadingActions.setTrue();

      try {
        const result = await gateAcpe(pendingRequests, duplicateHandling);

        if (result?.[FORM_ERROR]) {
          setConfirmError(result[FORM_ERROR]);
          return;
        }

        confirmOpenActions.setFalse();
        setPreview(null);
        setConfirmError(null);
      } catch (err: unknown) {
        setConfirmError(translateError(err, t));
      } finally {
        confirmLoadingActions.setFalse();
      }
    },
    onCancel: () => {
      if (!confirmLoading) {
        confirmOpenActions.setFalse();
        reset();
      }
    },
  };

  const acpeValidation = {
    open: acpeOpen,
    // The registry check is per course number, so one representative ACPE record drives it.
    request:
      pendingRequests.find(
        (request) => request.qualificationId === QualificationIds.Acpe,
      ) ?? null,
    onSave: async (
      releaseDate?: Date,
      expirationDate?: Date,
      contactHours?: number,
    ) => {
      const overridden = pendingRequests.map((request) => ({
        ...request,
        releaseDate: releaseDate ?? request.releaseDate,
        creditSubmissionExpirationDate:
          expirationDate ?? request.creditSubmissionExpirationDate,
        credits: contactHours ?? request.credits,
      }));

      const result = await save(overridden, pendingDuplicateHandling);

      if (!result?.[FORM_ERROR]) {
        acpeOpenActions.setFalse();
      }

      return result;
    },
    onClose: () => {
      acpeOpenActions.setFalse();
      reset();
    },
  };

  return { submit, internalValidation, bulkConfirm, acpeValidation };
};
